/** CLAÈ – Public Model Day booking page.
 * Anyone with the shareable token link can view the day's available
 * time slots (grouped by treatment) and complete a real booking.
 * Uses the standard /bookings POST with model_slot_lock so consent
 * forms + 48h/24h reminders + confirmation emails flow automatically. */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const gbp = (n) => "£" + Number(n || 0).toFixed(2);

export default function ModelDayPublic() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | ready | expired | notfound
  const [payload, setPayload] = useState(null);
  const [selected, setSelected] = useState(null); // {treatment, slot}
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", postcode: "", dob: "",
    emergency_contact: "", heard_about: "", marketing_consent: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/model-day/${token}`)
      .then((r) => { setPayload(r.data); setState("ready"); })
      .catch((e) => {
        if (e?.response?.status === 410) setState("expired");
        else setState("notfound");
      });
  }, [token]);

  const totalTreatments = useMemo(
    () => (payload?.treatments || []).length,
    [payload]
  );
  const totalAvailable = useMemo(
    () => (payload?.treatments || []).reduce(
      (n, t) => n + (t.slots || []).filter((s) => s.status === "AVAILABLE").length,
      0
    ),
    [payload]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Pick a time slot first"); return; }
    const req = ["first_name", "last_name", "email", "phone"];
    for (const k of req) {
      if (!form[k]) { toast.error("Please complete all required fields"); return; }
    }
    setSubmitting(true);
    try {
      const r = await api.post("/bookings", {
        items: [{ treatment_id: selected.treatment.id, quantity: 1 }],
        date: payload.day.date,
        time: selected.slot.time,
        client: {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          postcode: form.postcode.trim(),
          dob: form.dob || null,
          emergency_contact: form.emergency_contact.trim(),
          heard_about: form.heard_about.trim(),
          marketing_consent: !!form.marketing_consent,
          medical_consent: true,
        },
        origin_url: window.location.origin,
        model_slot_lock: {
          slot_id: selected.slot.id,
          slot_token: selected.slot.slot_token,
          day_token: payload.day_token,
        },
      });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else if (r.data.reference) {
        navigate(`/models/confirmed?ref=${r.data.reference}`);
      } else {
        toast.error("Unexpected response — please contact the clinic.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed. Please try again.");
    }
    setSubmitting(false);
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="flex items-center gap-2 text-stone-600">
          <Loader2 className="animate-spin" size={16} />
          <span className="mono-label text-xs">LOADING</span>
        </div>
      </div>
    );
  }
  if (state === "notfound") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-4">
        <div className="max-w-md text-center">
          <p className="mono-label text-[10px] text-[#8A6D2A]">CLAÈ AESTHETICS</p>
          <h1 className="font-serif text-3xl mt-2">Link not found</h1>
          <p className="text-sm text-stone-600 mt-3">This Model Day link is not valid or the day has been cancelled.</p>
        </div>
      </div>
    );
  }
  if (state === "expired") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-4">
        <div className="max-w-md text-center">
          <p className="mono-label text-[10px] text-[#8A6D2A]">CLAÈ AESTHETICS</p>
          <h1 className="font-serif text-3xl mt-2">This Model Day has ended</h1>
          <p className="text-sm text-stone-600 mt-3">Please contact the clinic for future model opportunities.</p>
        </div>
      </div>
    );
  }

  const dateLabel = new Date(payload.day.date + "T00:00").toLocaleDateString("en-GB",
    { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="model-day-public-page">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <p className="mono-label text-[10px] text-[#8A6D2A]">CLAÈ AESTHETICS</p>
        <h1 className="font-serif text-4xl mt-2" data-testid="model-day-title">
          {payload.day.title || "Model Day"}
        </h1>
        <p className="text-stone-600 mt-1 text-lg">{dateLabel}</p>
        {payload.day.start_time && payload.day.end_time && (
          <p className="mono-label text-[10px] text-stone-500 mt-1">
            {payload.day.start_time}–{payload.day.end_time}
          </p>
        )}
        {payload.day.note && (
          <p className="text-sm text-stone-600 mt-4 max-w-2xl">{payload.day.note}</p>
        )}
        <p className="text-xs text-stone-500 mt-6">
          {totalAvailable} available slot{totalAvailable !== 1 ? "s" : ""} across {totalTreatments} treatment{totalTreatments !== 1 ? "s" : ""}.
          Model appointments are offered at a discounted rate in exchange for treatment participation.
        </p>

        {/* Treatment groups */}
        <div className="mt-8 space-y-6">
          {(payload.treatments || []).map((tt) => (
            <div key={tt.treatment.id} className="border hairline bg-white p-6" data-testid={`treatment-block-${tt.treatment.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-serif text-2xl">{tt.treatment.name}</h2>
                  {tt.treatment.short_description && (
                    <p className="text-sm text-stone-600 mt-2">{tt.treatment.short_description}</p>
                  )}
                  <p className="mono-label text-[10px] text-stone-500 mt-3">
                    STANDARD PRICE {gbp(tt.treatment.price)} · {tt.treatment.duration_min}m
                  </p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                {tt.slots.map((s) => {
                  const disabled = s.status !== "AVAILABLE";
                  const isSel = selected?.slot?.id === s.id;
                  return (
                    <button
                      key={s.id}
                      disabled={disabled}
                      data-testid={`slot-btn-${s.id}`}
                      onClick={() => setSelected({ treatment: tt.treatment, slot: s })}
                      className={`p-3 border transition text-left ${
                        disabled ? "opacity-40 cursor-not-allowed border-stone-200 bg-stone-50"
                        : isSel ? "border-stone-900 bg-stone-900 text-white"
                        : "hairline hover:border-stone-900"
                      }`}
                    >
                      <div className="font-medium">{s.time}</div>
                      <div className="mono-label text-[10px] mt-1">
                        {disabled ? s.status : (s.deposit_amount > 0 ? `DEP ${gbp(s.deposit_amount)} · MODEL ${gbp(s.model_price)}` : `MODEL ${gbp(s.model_price)}`)}
                      </div>
                    </button>
                  );
                })}
                {tt.slots.length === 0 && (
                  <p className="text-xs text-stone-500 col-span-full">No slots for this treatment.</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Selected + form */}
        {selected && (
          <form onSubmit={submit} className="mt-8 border hairline bg-white p-6" data-testid="model-day-form">
            <p className="mono-label text-[10px] text-[#8A6D2A]">SELECTED</p>
            <p className="font-serif text-2xl mt-1">
              {selected.treatment.name} · {selected.slot.time} · {gbp(selected.slot.model_price)}
            </p>
            <p className="text-xs text-stone-500 mt-1">{dateLabel}</p>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="First name *" value={form.first_name}
                     onChange={(v) => setForm({ ...form, first_name: v })} tid="input-first-name" />
              <Input label="Last name *" value={form.last_name}
                     onChange={(v) => setForm({ ...form, last_name: v })} tid="input-last-name" />
              <Input label="Email *" type="email" value={form.email}
                     onChange={(v) => setForm({ ...form, email: v })} tid="input-email" />
              <Input label="Mobile *" value={form.phone}
                     onChange={(v) => setForm({ ...form, phone: v })} tid="input-phone" />
              <Input label="Date of birth" type="date" value={form.dob}
                     onChange={(v) => setForm({ ...form, dob: v })} tid="input-dob" />
              <Input label="Postcode" value={form.postcode}
                     onChange={(v) => setForm({ ...form, postcode: v })} tid="input-postcode" />
              <Input label="Address" value={form.address} className="md:col-span-2"
                     onChange={(v) => setForm({ ...form, address: v })} tid="input-address" />
              <Input label="Emergency contact" value={form.emergency_contact} className="md:col-span-2"
                     onChange={(v) => setForm({ ...form, emergency_contact: v })} tid="input-emergency" />
              <Input label="How did you hear about us?" value={form.heard_about} className="md:col-span-2"
                     onChange={(v) => setForm({ ...form, heard_about: v })} tid="input-heard" />
            </div>

            <label className="flex items-start gap-2 mt-4 text-xs text-stone-600">
              <input type="checkbox" checked={form.marketing_consent}
                     onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} />
              I'm happy to receive occasional updates about CLAÈ Aesthetics.
            </label>

            <p className="text-xs text-stone-500 mt-4">
              Your treatment consent form will be emailed automatically after booking — please complete it before your appointment.
            </p>

            <button type="submit" disabled={submitting} data-testid="submit-model-day-booking"
                    className="mt-6 w-full bg-stone-900 text-white mono-label py-4 hover:bg-[#C5A059] transition disabled:opacity-60"
                    style={{ color: "#fff" }}>
              {submitting
                ? "PROCESSING…"
                : (selected.slot.deposit_amount > 0
                    ? `CONFIRM & PAY DEPOSIT ${gbp(selected.slot.deposit_amount)}`
                    : `CONFIRM BOOKING · ${gbp(selected.slot.model_price)} IN CLINIC`)}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", tid, className = "" }) {
  return (
    <label className={`block text-xs text-stone-600 ${className}`}>
      <span className="mono-label text-[10px] text-stone-500">{label}</span>
      <input type={type} value={value} data-testid={tid}
             onChange={(e) => onChange(e.target.value)}
             className="w-full border hairline p-2 text-sm mt-1" />
    </label>
  );
}
